import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSpendMetrics } from '@/api/clarity'
import { mockDataHandler } from '@/api/clarity/mockSpend/mockDatahandler'
import { formatMetric } from '@/hooks/clarity/util'
import { SpendRegionBoxMetricsModel } from '@/types/clarity'
import { dateToString, getMappedRegionBranches } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useSpendRegionBoxMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<SpendRegionBoxMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<
    SpendRegionBoxMetricsModel[]
  >([])
  const { regionBranchMap, branches } = getMappedRegionBranches(true)
  const [processedData, setProcessedData] = useState<
    SpendRegionBoxMetricsModel[]
  >([])

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { spendRegionBoxMetrics } = await fetchSpendRegionBoxMetrics(
          branches,
          startDate,
          endDate,
          regionBranchMap
        )

        const filteredMetrics = spendRegionBoxMetrics.filter(
          (metric) => metric.stand !== 'system_total'
        )

        setBranchData(filteredMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { spendRegionBoxMetrics } = await fetchSpendRegionBoxMetrics(
          branches,
          dateToString(trendingDate, 'from', 'yyyy-mm'),
          dateToString(trendingDate, 'to', 'yyyy-mm'),
          regionBranchMap
        )

        const filteredMetrics = spendRegionBoxMetrics.filter(
          (metric) =>
            metric.stand !== 'system_total' && metric.region !== 'Other'
        )

        setBranchTrendData(filteredMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, dateFilterType])

  const getProcessedData = useCallback(
    (
      currentData: SpendRegionBoxMetricsModel[],
      trendData: SpendRegionBoxMetricsModel[]
    ) => {
      if (!currentData.length || !trendData.length) return []

      const currentDataMap = new Map(
        currentData.map((item) => [item.stand, item])
      )
      const trendDataMap = new Map(trendData.map((item) => [item.stand, item]))
      const commonStands = [...currentDataMap.keys()].filter((stand) =>
        trendDataMap.has(stand)
      )

      const standsByRegion = commonStands.reduce((acc, stand) => {
        const region = currentDataMap.get(stand)?.region || ''
        const current = currentDataMap.get(stand)?.current
        const trend = trendDataMap.get(stand)?.current

        if (!acc.has(region)) {
          acc.set(region, [])
        }

        acc.get(region)?.push({
          stand: stand,
          region: region,
          current: {
            cogsPercentage: current?.cogsPercentage || 0,
            ptiPercentage: current?.ptiPercentage || 0,
            compliancePercentage: current?.compliancePercentage || 0,
            cogsVsTheoretical: current?.cogsVsTheoretical || 0,
          },
          prev: {
            cogsPercentage: trend?.cogsPercentage || 0,
            ptiPercentage: trend?.ptiPercentage || 0,
            compliancePercentage: trend?.compliancePercentage || 0,
            cogsVsTheoretical: trend?.cogsVsTheoretical || 0,
          },
        })

        return acc
      }, new Map<string, SpendRegionBoxMetricsModel[]>())

      const sortedStandsByRegion = new Map(
        [...standsByRegion.entries()].sort((a, b) => {
          const regionA = a[0].toLowerCase()
          const regionB = b[0].toLowerCase()
          return regionA.localeCompare(regionB)
        })
      )

      // Create parent regions with children stands
      const processedData = Array.from(sortedStandsByRegion.entries()).map(
        ([region, stands]) => {
          // Create parent region with aggregated metrics
          const regionData: SpendRegionBoxMetricsModel = {
            stand: region,
            current: {
              cogsPercentage:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.cogsPercentage || 0),
                  0
                ) / stands.length,
              ptiPercentage:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.ptiPercentage || 0),
                  0
                ) / stands.length,
              compliancePercentage:
                stands.reduce(
                  (sum, stand) =>
                    sum + (stand.current?.compliancePercentage || 0),
                  0
                ) / stands.length,
              cogsVsTheoretical:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.cogsVsTheoretical || 0),
                  0
                ) / stands.length,
            },
            prev: {
              cogsPercentage:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.cogsPercentage || 0),
                  0
                ) / stands.length,
              ptiPercentage:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.ptiPercentage || 0),
                  0
                ) / stands.length,
              compliancePercentage:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.compliancePercentage || 0),
                  0
                ) / stands.length,
              cogsVsTheoretical:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.cogsVsTheoretical || 0),
                  0
                ) / stands.length,
            },
            children: stands,
          }

          return regionData
        }
      )

      setProcessedData(processedData as SpendRegionBoxMetricsModel[])
    },
    [branchData, branchTrendData]
  )

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching spend region box data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (branchData.length && branchTrendData.length) {
      getProcessedData(branchData, branchTrendData)
    }
  }, [branchData, branchTrendData])

  return {
    processedData,
    loading,
    error,
    refetch,
  }
}

async function fetchSpendRegionBoxMetrics(
  branches: string[],
  startDate: string,
  endDate: string,
  regionBranchMap: Map<string, string[]>
): Promise<{
  spendRegionBoxMetrics: SpendRegionBoxMetricsModel[]
}> {
  try {
    const response = await getSpendMetrics(
      branches.join(','),
      startDate,
      endDate
    )

    const mockResponse = await mockDataHandler('All', startDate, endDate)

    const spendRegionBoxMetrics: SpendRegionBoxMetricsModel[] = response[
      'branch_data'
    ].map((responseMetrics: any) => {
      return setMetric(
        responseMetrics.dcn,
        getBranchRegion(responseMetrics.dcn, regionBranchMap),
        responseMetrics.cogs_percentage,
        responseMetrics.pti_percentage,
        responseMetrics.spend_compliance_percentage,
        getSpecificBranchData(responseMetrics.dcn, mockResponse).cogsDifference
      )
    })

    return {
      spendRegionBoxMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function getSpecificBranchData(
  branch: string,
  mockResponse: any
): {
  cogs: number
  cogsDifference: number
} {
  const branchData = mockResponse.cogsVsTheoretical.find(
    (data: any) => data.location === branch
  )

  return {
    cogs: branchData?.actualCogs || 0,
    cogsDifference: branchData?.cogsDifference || 0,
  }
}

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function setMetric(
  dcn: string,
  region: string,
  cogsPercentage: number,
  ptiPercentage: number,
  compliancePercentage: number,
  cogsVsTheoretical: number
): SpendRegionBoxMetricsModel {
  return {
    stand: dcn,
    region,
    current: {
      cogsPercentage: formatMetric(cogsPercentage, 1),
      ptiPercentage: formatMetric(ptiPercentage, 1),
      compliancePercentage: formatMetric(compliancePercentage, 1),
      cogsVsTheoretical: formatMetric(cogsVsTheoretical, 2),
    },
  }
}
